import { useCallback, useEffect, useMemo, useState } from "react";
import { useClinicContext } from "../../contexts";
import { SearchInput, SelectControl } from "../Common";
import { DOCTOR_TABLE_PAGE_SIZE } from "../../constants";
import Alert from "@mui/material/Alert";
import { BPDoctor } from "../../models";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";

type Filters = {
	clinicId: string;
	searchTerm: string;
	hideLinked: boolean;
};

interface AvailableBpDoctorsProps {
	doctorId: number;
	bpDoctors: BPDoctor[];
	onLink: (bp: BPDoctor) => void;
	onImport: () => void;
	fetchDoctorInstances: (
		clinicId: string,
		searchTerm: string,
		hideLinked: boolean,
		page: number
	) => Promise<BPDoctor[]>;
}
export const AvailableBpDoctors = ({
	doctorId,
	bpDoctors,
	onLink,
	onImport,
	fetchDoctorInstances
}: AvailableBpDoctorsProps) => {
	const { userClinics } = useClinicContext();
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [pagedBPDoctors, setPagedBPDoctors] = useState<BPDoctor[]>([]);
	const [filters, setFilters] = useState<Filters>({
		clinicId: "",
		searchTerm: "",
		hideLinked: true
	});

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError("");
		setPagedBPDoctors([]);
		fetchDoctorInstances(
			filters.clinicId,
			filters.searchTerm,
			filters.hideLinked,
			page
		)
			.then((doctors) => {
				if (!cancelled) setPagedBPDoctors(doctors);
			})
			.catch(() => {
				if (!cancelled)
					setError("Unable to load available Best Practice doctors.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [fetchDoctorInstances, filters, page]);

	const filteredBpDoctors = useMemo(
		() =>
			pagedBPDoctors.filter(
				(bp) =>
					bp.doctorId !== doctorId &&
					!bpDoctors.some((linked) => linked.id === bp.id)
			),
		[pagedBPDoctors, doctorId, bpDoctors]
	);

	const handleSearchValueChange = useCallback((searchTerm: string) => {
		setFilters((prev) => ({ ...prev, searchTerm }));
		setPage(1);
	}, []);

	const handleClinicChange = useCallback((clinicId: string) => {
		setFilters((prev) => ({ ...prev, clinicId }));
		setPage(1);
	}, []);

	const toggleFilter = useCallback(
		(name: string, checked: boolean) => {
			switch (name) {
				case "hideLinked":
					setPage(1);
					setFilters((prev) => ({ ...prev, hideLinked: checked }));
					break;
				default:
					break;
			}
		},
		[setFilters]
	);

	return (
		<>
			<div className="flex justify-between">
				<div className="ch-m-text ch-em-text">
					Available Best Practice Doctors
				</div>

				<button
					className="ch-button ch-button-tertiary"
					onClick={() => onImport()}
				>
					Import Doctors from Best Practice
				</button>
			</div>
			<div className="flex flex-row gap-4">
				<div style={{ width: 400 }}>
					<SearchInput
						label="Name"
						handleSearchValueChange={handleSearchValueChange}
					/>
				</div>
				<div style={{ width: 300 }}>
					<SelectControl
						label="Centre"
						options={[
							{
								label: "All",
								value: ""
							}
						].concat(
							(userClinics ?? []).map((clinic) => ({
								label: clinic.name,
								value: clinic.id.toString()
							}))
						)}
						value={filters.clinicId}
						handleChange={handleClinicChange}
					/>
				</div>

				<div className="ch-centre-checkbox" style={{ width: "unset" }}>
					<Checkbox
						checked={filters.hideLinked}
						onChange={(e, checked) => toggleFilter(`hideLinked`, checked)}
					/>
					Hide Linked BP Doctors
				</div>
			</div>
			{loading && <div role="status">Loading doctors...</div>}
			{error && <Alert severity="error">{error}</Alert>}
			{!loading && !error && filteredBpDoctors.length === 0 && (
				<Alert severity="info">
					There are no more available Best Practice doctors
				</Alert>
			)}
			{filteredBpDoctors && filteredBpDoctors.length > 0 && (
				<>
					<table className="ch-table" style={{ minWidth: "1000px" }}>
						<thead>
							<tr>
								<th>Salutation</th>
								<th>Display Name</th>
								{!filters.hideLinked && <th>Linked Doctor</th>}
								<th>Centre</th>
								<th style={{ textAlign: "center" }}>Add</th>
							</tr>
						</thead>
						<tbody>
							{filteredBpDoctors.map((bp) => (
								<tr key={bp.id}>
									<td>{bp.salutation}</td>
									<td>{bp.fullName}</td>
									{!filters.hideLinked && <td>{bp.doctorName}</td>}
									<td>{bp.clinicName}</td>
									<td className="text-center">
										{!bp.doctorId && (
											<Link underline="none" onClick={() => onLink(bp)}>
												Add
											</Link>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}
			<div className="flex justify-center gap-4 py-4">
				<button
					type="button"
					disabled={loading || page === 1}
					onClick={() => setPage(page - 1)}
				>
					Previous
				</button>
				<span>Page {page}</span>
				<button
					type="button"
					disabled={
						loading || !!error || pagedBPDoctors.length < DOCTOR_TABLE_PAGE_SIZE
					}
					onClick={() => setPage(page + 1)}
				>
					Next
				</button>
			</div>
		</>
	);
};
